import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { GoogleLogin, GoogleOAuthProvider } from '@react-oauth/google';
import { motion } from 'framer-motion';
import { useAuth } from '@/contexts/AuthContext';
import { PLAN_PRICES } from '@/config/pricing';
import { safeOAuthNext, safeResumeNext } from '@/lib/oauthNext';
import axios from 'axios';
import '@/styles/forms.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

const PLAN_CONTEXT = {
  starter: {
    headline: 'Get Your Full UX Audit',
    subtitle: `Sign in to purchase and run a Starter audit for $${PLAN_PRICES.STARTER.usd}`,
    features: [
      'Full detailed UX report',
      'PDF download',
      'Annotated screenshots',
      'Fix recommendations & severity prioritization',
      "One free audit every month, pay only when you're ready",
    ],
    redirectAfterLogin: '/audit/new',
  },
  default: {
    headline: 'Welcome to UXAuditPro',
    subtitle: 'Sign in to access AI-powered UX audits',
    features: [
      'AI-powered UX analysis across 6 dimensions',
      'Detailed reports with actionable insights',
      'PDF exports with annotated screenshots',
      'One free audit every month',
    ],
    redirectAfterLogin: '/dashboard',
  },
};

export default function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const planParam = searchParams.get('plan');
  const ctx = PLAN_CONTEXT[planParam] || PLAN_CONTEXT.default;
  const next = searchParams.get('next');
  const loginNext = safeOAuthNext(next) || safeResumeNext(next);

  const { user, loading: authLoading, setUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Already signed in and an app asked to connect (or a recovery link sent
  // them here): go straight back, or the flow dead-ends on a sign-in button.
  // replace, not assign, so Back does not return here and bounce again.
  useEffect(() => {
    if (!authLoading && user && loginNext) window.location.replace(loginNext);
  }, [authLoading, user, loginNext]);

  const handleGoogleSuccess = async (credentialResponse) => {
    setLoading(true);
    setError('');

    try {
      const resp = await axios.post(
        `${API}/auth/google/token`,
        { credential: credentialResponse.credential },
        { withCredentials: true }
      );

      if (resp.data.success) {
        setUser(resp.data.user);
        // An app's connect request, or a checkout-recovery link: a full
        // navigation, because /oauth/ is served by the backend, not this app.
        if (loginNext) {
          window.location.assign(loginNext);
          return;
        }
        navigate(ctx.redirectAfterLogin);
      }
    } catch (e) {
      console.error('Login failed:', e);
      setError(e.response?.data?.detail || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleError = () => {
    setError('Google sign-in was cancelled or failed. Please try again.');
  };

  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <div className="min-h-screen flex flex-col">
        {/* Header */}
        <header className="p-6">
          <Link to="/" className="inline-flex items-center gap-2.5" style={{ minHeight: 'var(--target-min)' }}>
            <span
              className="flex items-center justify-center flex-none"
              style={{ width: '30px', height: '30px', borderRadius: '9px', background: 'var(--accent)', color: 'var(--on-accent)', fontWeight: 'var(--type-weight-mark)', fontSize: '12px' }}
              aria-hidden="true"
            >
              UX
            </span>
            <span style={{ fontWeight: 'var(--type-h3-weight)', fontSize: 'var(--type-h3-size)', letterSpacing: 'var(--type-h3-tracking)', color: 'var(--ink-1)' }}>
              UXAuditPro
            </span>
          </Link>
        </header>

        {/* Main Content */}
        <main className="flex-1 flex items-center justify-center px-4 py-8">
          {/* NO OPACITY FADE ON THE WAY IN. THE SLIDE STAYS.
            *
            * Same change, same reason, as NewAuditPage.js, which was measured
            * on production 2026-08-27 stranded at opacity 0.235305 with its
            * form unusable. This wrapper is the same shape: an entry animation
            * standing between a person and the only surface on the page.
            *
            * The argument is STRONGER here, not weaker. This file contains no
            * <input> — it is OAuth buttons — and that does not soften it. This
            * is the only door into the product; stranded, there is nothing to
            * click and no way in.
            *
            * Transform is safe to strand. Opacity is not. A halted frame loop
            * now leaves a readable, clickable login card 20px off-place.
            *
            * Guarded by styles/__tests__/no-stranded-input-surface.test.js. */}
          <motion.div
            initial={{ y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="w-full"
            style={{ maxWidth: '480px' }}
          >
            {/* Login Card */}
            <div className="uxa-form-card">
              <div className="uxa-form-card-spectrum" aria-hidden="true" />
              <div style={{ padding: '40px' }}>
                <div className="flex items-center gap-2.5">
                  <span
                    className="flex items-center justify-center flex-none"
                    style={{ width: '34px', height: '34px', borderRadius: '10px', background: 'var(--accent)', color: 'var(--on-accent)', fontWeight: 'var(--type-weight-mark)', fontSize: '13px' }}
                    aria-hidden="true"
                  >
                    UX
                  </span>
                  <span style={{ fontWeight: 700, fontSize: '24px', letterSpacing: '-0.02em', color: 'var(--ink-1)' }}>UXAuditPro</span>
                </div>

                <h1 style={{ fontSize: 'var(--type-h2-size)', fontWeight: 'var(--type-h2-weight)', lineHeight: 'var(--type-h2-leading)', letterSpacing: 'var(--type-h2-tracking)', color: 'var(--ink-1)', margin: '28px 0 0' }}>
                  {ctx.headline}
                </h1>
                <p className="uxa-helper" style={{ marginTop: '8px' }}>
                  {ctx.subtitle}
                </p>

                <div className="flex flex-col" style={{ gap: '18px', marginTop: '24px' }}>
                  {/* The error banner sits directly above the action, where focus
                      lands after a failed attempt. */}
                  {error && (
                    <div
                      role="alert"
                      className="uxa-msg uxa-msg--error"
                      style={{ padding: '12px 14px', border: '2px solid var(--danger)', borderRadius: 'var(--radius-field)', alignItems: 'center' }}
                      data-testid="login-error"
                    >
                      <span className="uxa-msg-mark" aria-hidden="true">!</span>
                      <span><strong>Error:</strong> {error}</span>
                    </div>
                  )}

                  {/* Google Sign In */}
                  <div className="flex justify-center">
                    {loading ? (
                      <div
                        className="uxa-button-secondary"
                        style={{ width: '100%', height: 'var(--field-height-lg)' }}
                        aria-live="polite"
                      >
                        <span
                          className="uxa-spinner"
                          style={{ borderColor: 'var(--border-2)', borderTopColor: 'var(--ink-1)' }}
                        />
                        <span style={{ color: 'var(--ink-2)' }}>Signing in…</span>
                      </div>
                    ) : (
                      <GoogleLogin
                        onSuccess={handleGoogleSuccess}
                        onError={handleGoogleError}
                        theme="outline"
                        size="large"
                        width="100%"
                        text="continue_with"
                        shape="pill"
                        logo_alignment="left"
                        useOneTap
                        data-testid="google-login-btn"
                      />
                    )}
                  </div>

                  <div className="uxa-or">what you get</div>

                  {/* Features Preview */}
                  <div style={{ background: 'var(--surface-panel)', border: '1px solid var(--border-2)', borderRadius: 'var(--radius-md)', padding: '18px 20px' }}>
                    <ul className="space-y-3" style={{ margin: 0, padding: 0, listStyle: 'none' }}>
                      {ctx.features.map((feature, i) => (
                        <li key={i} className="flex items-start gap-3" style={{ fontSize: '14px', lineHeight: 1.5, color: 'var(--ink-1)' }}>
                          <span
                            className="flex-none flex items-center justify-center"
                            style={{ width: '20px', height: '20px', borderRadius: '50%', background: 'var(--accent)', color: 'var(--on-accent)', marginTop: '1px' }}
                            aria-hidden="true"
                          >
                            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                            </svg>
                          </span>
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                {/* Terms */}
                <p className="uxa-helper" style={{ marginTop: '20px' }}>
                  By continuing, you agree to our{' '}
                  <Link to="/terms/" style={{ color: 'var(--link)', fontWeight: 700, textDecoration: 'underline', textUnderlineOffset: '3px' }}>
                    Terms of Service
                  </Link>
                  {' '}and{' '}
                  <Link to="/privacy/" style={{ color: 'var(--link)', fontWeight: 700, textDecoration: 'underline', textUnderlineOffset: '3px' }}>
                    Privacy Policy
                  </Link>
                </p>
              </div>
            </div>

            {/* Back to Home */}
            <div className="text-center mt-6">
              <Link
                to="/"
                className="inline-flex items-center justify-center gap-2"
                style={{ minHeight: 'var(--target-min)', fontSize: '14px', fontWeight: 500, color: 'var(--ink-2)' }}
              >
                <span aria-hidden="true">&larr;</span> Back to Home
              </Link>
            </div>
          </motion.div>
        </main>

        {/* Footer */}
        <footer className="p-6 text-center">
          <p className="uxa-meta">
            &copy; {new Date().getFullYear()} UXAuditPro. All rights reserved.
          </p>
        </footer>
      </div>
    </GoogleOAuthProvider>
  );
}
